<template>
    <div class="chatbot-state-viewer">
        <!-- Toolbar -->
        <div class="viewer-toolbar">
            <div class="toolbar-controls">
                <a-button 
                    :disabled="currentStepIndex === 0" 
                    @click="rewind"
                    size="small"
                    type="default"
                >
                    <span class="material-icons" style="font-size: 18px;">replay</span>
                </a-button>
                <a-button 
                    :disabled="currentStepIndex === 0" 
                    @click="goToPreviousStep"
                    size="small"
                    type="default"
                >
                    <span class="material-icons" style="font-size: 18px;">skip_previous</span>
                </a-button>
                <a-button 
                    @click="togglePlayPause"
                    size="small"
                    type="primary"
                >
                    <span class="material-icons" style="font-size: 18px;">
                        {{ isPlaying ? 'pause' : 'play_arrow' }}
                    </span>
                </a-button>
                <a-button 
                    :disabled="currentStepIndex >= totalSteps - 1" 
                    @click="goToNextStep"
                    size="small"
                    type="default"
                >
                    <span class="material-icons" style="font-size: 18px;">skip_next</span>
                </a-button>
            </div>
            <div class="toolbar-info">
                <span class="step-counter">
                    {{
                        $t("message.chatbotStateViewerStep", {
                            current: currentStepIndex + 1,
                            total: totalSteps,
                        })
                    }}
                </span>
            </div>
        </div>

        <!-- Diagram Map -->
        <div 
            class="diagram-map"
            @wheel="handleWheel"
            @mousemove="handleMove"
            @mousedown="mousePressed = true"
            @mouseup="mousePressed = false"
            :style="{ cursor: mousePressed ? 'grabbing' : 'grab' }"
        >
            <div class="map-wrapper" :style="{ transform }">
                <!-- Block Connectors -->
                <svg 
                    class="connectors-layer" 
                    v-if="blocks && blocks.blocks && blocks.blocks.length > 0"
                    :width="svgWidth"
                    :height="svgHeight"
                >
                    <defs>
                        <linearGradient id="gradient-start" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#10b981;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#059669;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="gradient-ai" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#06b6d4;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#0891b2;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="gradient-flow" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#7c3aed;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="gradient-message" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#2563eb;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="gradient-input" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#d97706;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="gradient-default" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#9ca3af;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#6b7280;stop-opacity:1" />
                        </linearGradient>
                    </defs>
                    <!-- Connectors - tüm path'ler -->
                    <path 
                        v-for="connector in connectors" 
                        :key="connector.id"
                        :d="connector.path"
                    />
                    <!-- Active connectors - üstte render et -->
                    <path 
                        v-for="connector in connectors.filter(c => isActiveConnector(c.targetId))" 
                        :key="connector.id + '-active'"
                        :d="connector.path"
                        class="connector-active"
                    />
                </svg>

                <!-- Blocks -->
                <template v-for="block in (blocks && blocks.blocks ? blocks.blocks : [])">
                    <a-tooltip
                        v-if="isBlockNavigable(block)"
                        :key="String(block.id) + '-nav'"
                        placement="top"
                    >
                        <template slot="title">{{ $t("chatbotv2.clickToOpenBlock") }}</template>
                        <div
                            class="cb-block"
                            :class="{
                                'block-passed': passedBlockIds.includes(block.id) && block.id !== currentBlockId,
                                'block-current': block.id === currentBlockId,
                                'block-done': block.id === currentBlockId && isLastStep,
                                'block-future': !passedBlockIds.includes(block.id) && block.id !== currentBlockId
                            }"
                            :style="getBlockStyle(block)"
                            :data-type="block.type"
                            @click.stop="onBlockNavigate(block)"
                            @mousedown.stop
                        >
                            <div class="block-caption">
                                <div class="block-caption-name">{{ getBlockDisplayName(block) }}</div>
                            </div>
                            <div class="block-content">
                                <div class="d-flex align-items-center align-content-center justify-content-start block-icon">
                                    <BlockIconPreview :block-type="block.type" />
                                    <span class="blockTypeText">{{ getBlockTypeText(block.type) }}</span>
                                </div>
                            </div>
                            <div v-if="block.id === currentBlockId && !isLastStep" class="current-indicator">
                                <div class="pulse-dot"></div>
                            </div>
                            <div v-if="block.id === currentBlockId && isLastStep" class="done-indicator">
                                <span class="done-checkmark">✓</span>
                                <span class="done-text">DONE</span>
                            </div>
                            <div v-if="passedBlockIds.includes(block.id) && block.id !== currentBlockId" class="passed-indicator">
                                <span class="material-icons">check_circle</span>
                            </div>
                        </div>
                    </a-tooltip>
                    <div
                        v-else
                        :key="String(block.id) + '-static'"
                        class="cb-block cb-block--no-navigate"
                        :class="{
                            'block-passed': passedBlockIds.includes(block.id) && block.id !== currentBlockId,
                            'block-current': block.id === currentBlockId,
                            'block-done': block.id === currentBlockId && isLastStep,
                            'block-future': !passedBlockIds.includes(block.id) && block.id !== currentBlockId
                        }"
                        :style="getBlockStyle(block)"
                        :data-type="block.type"
                        @mousedown.stop
                    >
                        <div class="block-caption">
                            <div class="block-caption-name">{{ getBlockDisplayName(block) }}</div>
                        </div>
                        <div class="block-content">
                            <div class="d-flex align-items-center align-content-center justify-content-start block-icon">
                                <BlockIconPreview :block-type="block.type" />
                                <span class="blockTypeText">{{ getBlockTypeText(block.type) }}</span>
                            </div>
                        </div>
                        <div v-if="block.id === currentBlockId && !isLastStep" class="current-indicator">
                            <div class="pulse-dot"></div>
                        </div>
                        <div v-if="block.id === currentBlockId && isLastStep" class="done-indicator">
                            <span class="done-checkmark">✓</span>
                            <span class="done-text">DONE</span>
                        </div>
                        <div v-if="passedBlockIds.includes(block.id) && block.id !== currentBlockId" class="passed-indicator">
                            <span class="material-icons">check_circle</span>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import BlockIconPreview from "../../views/Pages/Chatbot/pages/dashboard/common/BlockIconPreview.vue";
import BLOCK_TYPES from "../../views/Pages/Chatbot/block.types";
import { BlockStyles } from "../../views/Pages/Chatbot/chatbot";
import * as d3 from "d3";

export default {
    name: "ChatbotStateViewer",
    components: {
        BlockIconPreview,
    },
    props: {
        conversation: {
            type: Object,
            required: true
        }
    },
    computed: {
        ...mapGetters({
            getStoryById: "CB/getStoryById",
        }),
        chatbotState() {
            return this.$_.get(this.conversation, "chatbotState");
        },
        storyId() {
            return this.$_.get(this.chatbotState, "storyId");
        },
        storyVersion() {
            return this.$_.get(this.chatbotState, "storyVersion");
        },
        history() {
            return this.$_.get(this.chatbotState, "history", []);
        },
        currentPosition() {
            return this.$_.get(this.chatbotState, "position");
        },
        story() {
            if (!this.storyId) return null;
            return this.getStoryById(this.storyId);
        },
        board() {
            if (this.boardData) return this.boardData;
            if (!this.storyId || !this.storyVersion) return null;
            const boards = this.$store.state.CB.boards || [];
            const board = boards.find(b => {
                const boardStoryId = b.story && b.story.toString ? b.story.toString() : b.story;
                const storyIdStr = this.storyId.toString ? this.storyId.toString() : this.storyId;
                return boardStoryId === storyIdStr && b.version === this.storyVersion;
            });
            return board || null;
        },
        diagram() {
            if (!this.board || !this.board.diagram) return null;
            return this.board.diagram;
        },
        blocks() {
            if (!this.board || !this.diagram) return { blocks: [], metas: {} };
            
            const blockSizes = { width: 190, height: 40 };
            const squareBlockSizes = { width: 40, height: 40 };
            const squareBlockTypes = [BLOCK_TYPES.USER_INPUT, BLOCK_TYPES.ATTACHMENT_INPUT];
            const rootBlock = this.diagram;

            const metas = {
                [rootBlock.id]: {
                    width: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.width : blockSizes.width,
                    height: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.height : blockSizes.height,
                    name: rootBlock.name,
                    isRoot: true,
                    isSquare: squareBlockTypes.includes(rootBlock.type),
                },
            };
            const rootMeta = metas[rootBlock.id];

            const flatBlock = (block, parentMeta, isRoot) => {
                const isSquare = squareBlockTypes.includes(block.type);
                const sizes = isSquare ? squareBlockSizes : blockSizes;
                const meta = isRoot
                    ? parentMeta
                    : {
                          width: sizes.width,
                          height: sizes.height,
                          name: block.name,
                          isRoot: false,
                          isSquare,
                      };

                metas[block.id] = meta;
                
                const nextFlow = !isRoot && block.type === "FLOW";
                const collapsed = this.board.state && this.board.state.collapsed 
                    ? this.board.state.collapsed.includes(block.id) 
                    : false;

                const items = [
                    block,
                    ...(nextFlow ||
                    (collapsed && !["START_POINT"].includes(block.type))
                        ? []
                        : (block.children || [])
                              .map((childBlock, i) => {
                                  return !!childBlock ? flatBlock(childBlock, meta, false) : null;
                              })
                              .filter(Boolean)),
                ];

                return _.flatten(items);
            };

            const items = _.flatten(flatBlock(rootBlock, rootMeta, true));

            // AI_ASSISTANT bloğunu ekle (pozisyon hesaplamadan önce)
            const aiAssistantBlock = {
                id: "AI_ASSISTANT",
                name: "",
                type: "AI_ASSISTANT",
                children: [],
            };
            const aiAssistantBlockEnabled = true; // Her zaman göster
            if (aiAssistantBlockEnabled) {
                metas[aiAssistantBlock.id] = {
                    width: 190,
                    height: 40,
                    name: "",
                    isRoot: false,
                    isSquare: false,
                    x: 0,
                    y: 100,
                };
            }

            // Pozisyon hesaplama - DiagramViewer'daki gibi
            const calculatePositions = () => {
                const root = _.cloneDeep(rootBlock);
                const flatFlows = (block, isRoot = false) => {
                    if (!isRoot && block.type === BLOCK_TYPES.FLOW) block.children = [];
                    block.children = (block.children || []).filter(Boolean);
                    block.children.filter(Boolean).forEach(flatFlows);
                };
                flatFlows(root, true);

                let treemap = d3.tree().nodeSize([blockSizes.height * 2.5, blockSizes.width + 80]);

                let nodes = d3.hierarchy(root, function (d) {
                    return d.children;
                });
                nodes = treemap(nodes);
                
                const traverse = (block) => {
                    if (!!metas[block.data.id]) {
                        metas[block.data.id].x = block.y;
                        metas[block.data.id].y = block.x;
                    }
                    (block.children || []).filter(Boolean).forEach((child) => {
                        traverse(child);
                    });
                };
                traverse(nodes);
            };
            calculatePositions();

            // Board'dan gelen metas bilgilerini kullan (eğer varsa)
            if (this.board.metas) {
                Object.keys(metas).forEach(blockId => {
                    if (this.board.metas[blockId]) {
                        if (this.board.metas[blockId].x !== undefined) {
                            metas[blockId].x = this.board.metas[blockId].x;
                        }
                        if (this.board.metas[blockId].y !== undefined) {
                            metas[blockId].y = this.board.metas[blockId].y;
                        }
                    }
                });
            }

            // AI_ASSISTANT bloğunun pozisyonunu koru (board metas override etmesin)
            if (aiAssistantBlockEnabled && metas[aiAssistantBlock.id]) {
                metas[aiAssistantBlock.id].x = 0;
                metas[aiAssistantBlock.id].y = 100;
            }

            return {
                blocks: aiAssistantBlockEnabled ? [aiAssistantBlock, ...items] : items,
                metas,
            };
        },
        historySteps() {
            if (!this.blocks || !this.blocks.blocks) return [];
            return this.history.map(blockId => {
                const block = this.blocks.blocks.find(b => b.id === blockId);
                return {
                    blockId,
                    block: block || null,
                    type: block ? block.type : null
                };
            });
        },
        totalSteps() {
            return this.historySteps.length;
        },
        currentBlockId() {
            if (this.currentStepIndex < 0 || this.currentStepIndex >= this.historySteps.length) {
                return null;
            }
            return this.historySteps[this.currentStepIndex].blockId;
        },
        passedBlockIds() {
            return this.historySteps
                .slice(0, this.currentStepIndex)
                .map(step => step.blockId);
        },
        isLastStep() {
            return this.totalSteps > 0 && this.currentStepIndex === this.totalSteps - 1;
        },
        transform() {
            return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
        },
        diagramBounds() {
            if (!this.blocks || !this.blocks.metas || Object.keys(this.blocks.metas).length === 0) {
                return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
            }
            
            const metas = Object.values(this.blocks.metas);
            const xs = metas.map(m => (m.x != null ? m.x : 0));
            const ys = metas.map(m => (m.y != null ? m.y : 0));
            const widths = metas.map(m => (m.width != null ? m.width : 190));
            const heights = metas.map(m => (m.height != null ? m.height : 40));
            
            if (xs.length === 0 || ys.length === 0) {
                return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
            }
            
            return {
                minX: Math.min(...xs),
                maxX: Math.max(...xs.map((x, i) => x + widths[i])),
                minY: Math.min(...ys),
                maxY: Math.max(...ys.map((y, i) => y + heights[i]))
            };
        },
        svgWidth() {
            const bounds = this.diagramBounds;
            const width = bounds.maxX - bounds.minX;
            return Math.max(width + 100, 800);
        },
        svgHeight() {
            const bounds = this.diagramBounds;
            const height = bounds.maxY - bounds.minY;
            return Math.max(height + 100, 400);
        },
        connectors() {
            if (!this.blocks || !this.blocks.blocks || !this.blocks.metas) return [];
            
            const connectorMap = new Map(); // Duplicate'leri önlemek için
            
            // Tüm block'lar için connector'ları oluştur (sadece history'deki değil)
            this.blocks.blocks.forEach((block, i) => {
                if (block && block.children && block.children.length > 0) {
                    const isLast = i === this.blocks.blocks.length - 1;
                    if (isLast && block.type === "FLOW") return; // FLOW block'un son çocuğu için connector yok
                    
                    block.children.forEach((child) => {
                        const childId = this.getChildId(child);
                        const childBlock = typeof child === 'object' && child.id ? child : this.getBlockById(childId);
                        if (!childBlock) return;
                        
                        const connectorKey = `${block.id}-${childId}`;
                        
                        const connector = this.calculateConnectorForPath(block, childId, i);
                        if (connector) {
                            connector.targetId = childId; // Target block id'sini ekle
                            connectorMap.set(connectorKey, connector);
                        }
                    });
                }
            });
            
            return Array.from(connectorMap.values());
        },
    },
    data() {
        return {
            boardData: null,
            currentStepIndex: 0,
            isPlaying: false,
            playInterval: null,
            translateX: 0,
            translateY: 0,
            scale: 1,
            mousePressed: false,
            lastMouseX: 0,
            lastMouseY: 0,
        };
    },
    async mounted() {
        // Board'u yükle
        if (this.storyId && this.storyVersion) {
            // Önce store'dan kontrol et
            const boards = this.$store.state.CB.boards || [];
            const existingBoard = boards.find(b => {
                const boardStoryId = b.story && b.story.toString ? b.story.toString() : b.story;
                const storyIdStr = this.storyId.toString ? this.storyId.toString() : this.storyId;
                return boardStoryId === storyIdStr && b.version === this.storyVersion;
            });
            
            if (existingBoard) {
                this.boardData = existingBoard;
            } else {
                // Store'da yoksa API'den yükle
                try {
                    await this.$store.dispatch("CB/getStory", {
                        id: this.storyId,
                        version: this.storyVersion
                    });
                    // Store'a kaydedildikten sonra tekrar al
                    const updatedBoards = this.$store.state.CB.boards || [];
                    const loadedBoard = updatedBoards.find(b => {
                        const boardStoryId = b.story && b.story.toString ? b.story.toString() : b.story;
                        const storyIdStr = this.storyId.toString ? this.storyId.toString() : this.storyId;
                        return boardStoryId === storyIdStr && b.version === this.storyVersion;
                    });
                    if (loadedBoard) {
                        this.boardData = loadedBoard;
                    }
                } catch (error) {
                    console.error("Failed to load board:", error);
                }
            }
        }
        
        // İlk adımdan başla
        this.currentStepIndex = 0;
        // İlk block'a focuslan
        this.$nextTick(() => {
            this.focusCurrentBlock();
        });
    },
    beforeDestroy() {
        this.stopPlaying();
    },
    methods: {
        togglePlayPause() {
            if (this.isPlaying) {
                this.stopPlaying();
            } else {
                this.startPlaying();
            }
        },
        startPlaying() {
            this.isPlaying = true;
            // Her adım için connector animasyonunun tamamlanmasını bekle (2.5 saniye)
            this.playInterval = setInterval(() => {
                if (this.currentStepIndex < this.totalSteps - 1) {
                    this.currentStepIndex++;
                    this.focusCurrentBlock();
                } else {
                    this.stopPlaying();
                }
            }, 2500);
        },
        stopPlaying() {
            this.isPlaying = false;
            if (this.playInterval) {
                clearInterval(this.playInterval);
                this.playInterval = null;
            }
        },
        rewind() {
            this.stopPlaying();
            this.currentStepIndex = 0;
            this.focusCurrentBlock();
        },
        goToNextStep() {
            // Eğer play modundaysa pause yap
            if (this.isPlaying) {
                this.stopPlaying();
            }
            if (this.currentStepIndex < this.totalSteps - 1) {
                this.currentStepIndex++;
                this.focusCurrentBlock();
            }
        },
        goToPreviousStep() {
            // Geriye basınca pause yap
            if (this.isPlaying) {
                this.stopPlaying();
            }
            if (this.currentStepIndex > 0) {
                this.currentStepIndex--;
                this.focusCurrentBlock();
            }
        },
        focusCurrentBlock() {
            if (!this.currentBlockId || !this.blocks || !this.blocks.metas) return;
            const meta = this.blocks.metas[this.currentBlockId];
            if (!meta || meta.x == null || meta.y == null) return;
            
            const mapElement = this.$el.querySelector('.diagram-map');
            if (!mapElement) return;
            
            const mapRect = mapElement.getBoundingClientRect();
            const centerX = mapRect.width / 2;
            const centerY = mapRect.height / 2;
            
            const x = meta.x || 0;
            const y = meta.y || 0;
            const width = meta.width || 200;
            const height = meta.height || 80;
            
            // Smooth animasyon için
            const targetX = centerX - (x + width / 2) * this.scale;
            const targetY = centerY - (y + height / 2) * this.scale;
            
            // Smooth transition
            const duration = 800;
            const startX = this.translateX;
            const startY = this.translateY;
            const startTime = Date.now();
            
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const ease = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                
                this.translateX = startX + (targetX - startX) * ease;
                this.translateY = startY + (targetY - startY) * ease;
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            
            requestAnimationFrame(animate);
        },
        getChildId(child) {
            if (child == null) return '';
            return typeof child === 'object' && child.id != null ? child.id : String(child);
        },
        getBlockById(id) {
            if (!this.blocks || !this.blocks.blocks) return null;
            return this.blocks.blocks.find(b => b.id === id);
        },
        getBlockStyle(block) {
            if (!this.blocks || !this.blocks.metas || !this.blocks.metas[block.id]) {
                const style = BlockStyles[block.type] || BlockStyles.DEFAULT_FALLBACK;
                return {
                    left: '0px',
                    top: '0px',
                    width: '190px',
                    height: '40px',
                    ...style
                };
            }
            const meta = this.blocks.metas[block.id];
            const style = BlockStyles[block.type] || BlockStyles.DEFAULT_FALLBACK;
            return {
                left: (meta.x != null ? meta.x : 0) + 'px',
                top: (meta.y != null ? meta.y : 0) + 'px',
                width: (meta.width != null ? meta.width : 190) + 'px',
                height: (meta.height != null ? meta.height : 40) + 'px',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                ...style
            };
        },
        getBlockTypeText(type) {
            // Block type'ı i18n ile çevir
            try {
                return this.$t(`chatbotv2.${type}`);
            } catch (e) {
                return type;
            }
        },
        isBlockNavigable(block) {
            if (!block) return false;
            if (block.type === BLOCK_TYPES.START_POINT) return false;
            if (block.type === BLOCK_TYPES.AI_ASSISTANT || block.id === "AI_ASSISTANT") return false;
            return true;
        },
        getBlockDisplayName(block) {
            if (!block) return "";
            const n = block.name;
            if (n != null && String(n).trim() !== "") return String(n).trim();
            return this.getBlockTypeText(block.type);
        },
        onBlockNavigate(block) {
            if (!this.isBlockNavigable(block) || !block || !this.storyId || this.storyVersion == null) return;
            this.$router.push({
                path: "/chatbot/story/" + this.storyId + "/" + this.storyVersion + "/block/" + block.id,
                query: { autoFocus: "true" },
            });
        },
        calculateConnectorForPath(block, childId, stepIndex) {
            if (!this.blocks || !this.blocks.metas) return null;
            
            const sourceMeta = this.blocks.metas[block.id];
            const targetMeta = this.blocks.metas[childId];
            if (!sourceMeta || !targetMeta) return null;
            
            const sourceX = sourceMeta.x || 0;
            const sourceY = sourceMeta.y || 0;
            const sourceWidth = sourceMeta.width || 190;
            const sourceHeight = sourceMeta.height || 40;
            const targetX = targetMeta.x || 0;
            const targetY = targetMeta.y || 0;
            const targetWidth = targetMeta.width || 190;
            const targetHeight = targetMeta.height || 40;
            
            const childCount = block.children ? block.children.length : 0;
            const isOddBlockLength = childCount % 2 === 1;
            const middleIndex = (childCount - 1) / 2.0;
            const childIndex = block.children ? block.children.findIndex(c => this.getChildId(c) === childId) : -1;
            const isMiddleBlock = childCount === 1 || (isOddBlockLength && childIndex === middleIndex);
            const isCurved = isMiddleBlock ? false : childCount > 1;

            let path = "";
            if (isCurved) {
                const moveX = sourceX + sourceWidth + 5;
                const moveY = sourceY + sourceHeight / 2;
                const cX1 = sourceX + sourceWidth + 50;
                const cY1 = sourceY + sourceHeight / 2;
                const cX2 = targetX - 60;
                const cY2 = targetY + targetHeight / 2;
                const endX = targetX - (targetMeta.isSquare ? 15 : 0);
                const endY = targetY + targetHeight / 2;
                path = `M${moveX} ${moveY} C ${cX1} ${cY1} ${cX2} ${cY2} ${endX} ${endY}`;
            } else {
                path = `M${sourceX + sourceWidth + (sourceMeta.isSquare ? 15 : 10)} ${
                    sourceY + sourceHeight / 2
                } L ${targetX - (targetMeta.isSquare ? 15 : 10)} ${targetY + targetHeight / 2}`;
            }
            
            return {
                path,
                id: `${block.id}-${childId}`
            };
        },
        handleWheel(event) {
            event.preventDefault();
            const delta = event.deltaY * -0.001;
            const newScale = Math.min(Math.max(0.1, this.scale + delta), 3);
            this.scale = newScale;
        },
        handleMove(event) {
            if (!this.mousePressed) return;
            const deltaX = event.clientX - this.lastMouseX;
            const deltaY = event.clientY - this.lastMouseY;
            this.translateX += deltaX / this.scale;
            this.translateY += deltaY / this.scale;
            this.lastMouseX = event.clientX;
            this.lastMouseY = event.clientY;
        },
        isActiveConnector(targetBlockId) {
            // İlk blok ise (startBlockId) yeşil olmamalı
            if (this.currentStepIndex === 0) return false;
            
            // History'den mevcut adıma kadar olan block'ları al
            const selectedPaths = this.historySteps
                .slice(0, this.currentStepIndex + 1)
                .map(step => step.blockId);
            
            // Target block id history içinde mi kontrol et (ve ilk blok değil)
            return selectedPaths.includes(targetBlockId) && selectedPaths.indexOf(targetBlockId) > 0;
        }
    },
    watch: {
        currentStepIndex() {
            this.focusCurrentBlock();
        },
        mousePressed(newVal) {
            if (newVal) {
                const mapElement = this.$el.querySelector('.diagram-map');
                if (mapElement) {
                    const rect = mapElement.getBoundingClientRect();
                    this.lastMouseX = event.clientX;
                    this.lastMouseY = event.clientY;
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.chatbot-state-viewer {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: calc(100vh - 300px);
    background: #fff;
    
    .viewer-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e5e7eb;
        background: #fff;
        flex-shrink: 0;
        
        .toolbar-controls {
            display: flex;
            gap: 8px;
        }
        
        .toolbar-info {
            .step-counter {
                font-size: 14px;
                font-weight: 600;
                color: #6b7280;
            }
        }
    }
    
    .diagram-map {
        flex: 1;
        overflow: hidden;
        position: relative;
        background-image: linear-gradient(rgba(214, 214, 219, 0.2) 1px, transparent 1px),
            linear-gradient(to right, rgba(214, 214, 219, 0.4) 1px, rgba(139, 140, 140, 0.1) 1px) !important;
        background-size: 20px 20px;
        min-height: calc(100vh - 370px);
        
        .map-wrapper {
            position: relative;
            width: 100%;
            height: auto;
            transform-origin: 0 0;
            
            .connectors-layer {
                position: absolute;
                top: 0;
                left: 0;
                overflow: visible !important;
                pointer-events: none;
                z-index: 1;
                
                path {
                    fill: none;
                    stroke-width: 2.5px;
                    stroke: #c9d3de;
                    transition: all 0.7s;
                    
                    &.connector-active {
                        stroke: #22c55e;
                        stroke-width: 3px;
                        animation-name: lineanim;
                        animation-duration: 2.5s;
                        animation-timing-function: ease-in;
                        filter: drop-shadow(0 0 3px rgba(34, 197, 94, 0.5));
                    }
                }
                
                @keyframes lineanim {
                    0% {
                        stroke-dashoffset: 1000;
                        stroke-dasharray: 1000;
                    }
                    100% {
                        stroke-dashoffset: 0;
                    }
                }
            }
            
            .cb-block {
                position: absolute;
                width: 190px;
                height: 40px;
                min-height: 40px;
                align-items: center;
                border-radius: 20px;
                box-shadow: 0 2px 15px #00000012;
                display: flex;
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                user-select: none;
                cursor: pointer;
                z-index: 10;
                
                &.block-passed {
                    opacity: 0.7;
                    filter: brightness(0.9);
                }
                
                &.block-current {
                    box-shadow: 0 0 0 4px rgba(0, 111, 255, 0.3), 0 2px 15px #00000012;
                    z-index: 20;
                    animation: block-pulsar 2s ease-in-out infinite;
                }
                
                &.block-done {
                    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.3), 0 2px 15px #00000012;
                    z-index: 25;
                    animation: block-done-pulse 2s ease-in-out infinite;
                    border: 3px solid #22c55e;
                    transform: scale(1.05);
                }
                
                &.block-future {
                    opacity: 0.6;
                    filter: grayscale(0.3);
                }
                
                @keyframes block-pulsar {
                    0% {
                        box-shadow: 0 0 0 0 rgba(0, 111, 255, 0.4), 0 2px 15px #00000012;
                    }
                    70% {
                        box-shadow: 0 0 0 10px rgba(0, 111, 255, 0), 0 2px 15px #00000012;
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(0, 111, 255, 0), 0 2px 15px #00000012;
                    }
                }
                
                @keyframes block-done-pulse {
                    0% {
                        box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.3), 0 2px 15px #00000012;
                        transform: scale(1.05);
                    }
                    50% {
                        box-shadow: 0 0 0 12px rgba(34, 197, 94, 0.5), 0 0 30px rgba(34, 197, 94, 0.4), 0 2px 15px #00000012;
                        transform: scale(1.08);
                    }
                    100% {
                        box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.3), 0 2px 15px #00000012;
                        transform: scale(1.05);
                    }
                }

                &.cb-block--no-navigate {
                    cursor: default;

                    .block-content {
                        cursor: default;
                    }
                }
                
                .block-caption {
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 220px;
                    max-width: min(220px, 28vw);
                    margin-bottom: 4px;
                    text-align: center;
                    pointer-events: none;
                    z-index: 15;

                    .block-caption-name {
                        font-size: 12px;
                        font-weight: 700;
                        color: #374151;
                        line-height: 1.2;
                        max-height: 32px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        word-wrap: break-word;
                        transition: color 0.5s;
                    }
                }
                
                .block-content {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    text-align: center;
                    cursor: pointer;
                    padding-left: 5px;
                    
                    .block-icon {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        
                        :deep(.material-icons) {
                            font-size: 20px;
                        }
                        
                        :deep(.custom-block-logo) {
                            height: 24px;
                            width: 24px;
                            border-radius: 50%;
                        }
                        
                        .blockTypeText {
                            margin-left: 5px;
                            font-size: 12px;
                            font-weight: 600;
                            line-height: 20px;
                            overflow: hidden !important;
                            text-overflow: ellipsis;
                            transition: color 0.5s;
                            white-space: nowrap;
                        }
                    }
                }
                
                &[data-type="USER_INPUT"],
                &[data-type="ATTACHMENT_INPUT"] {
                    background: transparent !important;
                    display: flex;
                    justify-content: center;
                    height: 40px;
                    max-width: none;
                    min-width: auto;
                    width: 40px;
                    box-shadow: none !important;
                    
                    &:after {
                        position: absolute;
                        border: 2px solid #c9d3de;
                        border-radius: 8px;
                        content: "";
                        flex-shrink: 0;
                        height: 100%;
                        transform: rotate(45deg);
                        transition: background-color 0.5s, border 0.5s;
                        width: 100%;
                        top: 0 !important;
                        z-index: -1;
                        left: 0 !important;
                    }
                    
                    .block-icon .blockTypeText {
                        display: none;
                    }
                }
                
                &[data-type="START_POINT"],
                &[data-type="AI_ASSISTANT"],
                &[data-type="FEEDBACK_AI_ASSISTANT"],
                &[data-type="INSERT_TO_SYSTEM_PROMPT"],
                &[data-type="FILTER"],
                &[data-type="SEND_TRANSCRIPT"],
                &[data-type="SEND_SMS"],
                &[data-type="SEND_MAIL"],
                &[data-type="CREATE_EVENT"],
                &[data-type="CREATE_RESERVATION"],
                &[data-type="RESERVATION_OPEN_URL"],
                &[data-type="RESERVATION_SELECT_DAY"],
                &[data-type="RESERVATION_SELECT_TIME"],
                &[data-type="RESERVATION_EVENT_LIST"],
                &[data-type="DEFAULT_FALLBACK"],
                &[data-type="SET_ATTRIBUTE"],
                &[data-type="CREATE_TICKET"],
                &[data-type="WEBHOOK"],
                &[data-type="API_REQUEST"],
                &[data-type="CREATE_TASK"],
                &[data-type="SUPPRESSION_ADD"],
                &[data-type="HOLIDAY_PLUS_GET_HOTELS"],
                &[data-type="HOLIDAY_PLUS_GET_ROOMS"],
                &[data-type="ZOHO_CREATE_RECORD"],
                &[data-type="SUPPRESSION_REMOVE"],
                &[data-type="AI_FUNCTIONS"],
                &[data-type="TIMEOUT"],
                &[data-type="TRANSFER_TO_CHATBOT"],
                &[data-type="TABLE_CREATE_RECORD"],
                &[data-type="TABLE_DELETE_RECORD"],
                &[data-type="TABLE_UPDATE_RECORD"],
                &[data-type="TABLE_FIND_RECORD"],
                &[data-type="TABLE_FIND_RECORDS"],
                &[data-type="INSTAGRAM_COMMENT_REPLY"],
                &[data-type="INSTAGRAM_COMMENT_DELETE"],
                &[data-type="INSTAGRAM_COMMENT_SHOW"],
                &[data-type="INSTAGRAM_COMMENT_HIDE"],
                &[data-type="INSTAGRAM_COMMENT_REPLY_AS_PRIVATE"],
                &[data-type="INSTAGRAM_SEND_MESSAGE"],
                &[data-type="MESSENGER_SEND_MESSAGE"],
                &[data-type="TICIMAX_V2_SELECT_ORDER"],
                &[data-type="TICIMAX_V2_ORDER_STATUS"],
                &[data-type="CUSTOM_BLOCK"],
                &[data-type="CACHE_WRITE"],
                &[data-type="CACHE_READ"],
                &[data-type="CACHE_EXTEND"],
                &[data-type="CACHE_DELETE"],
                &[data-type="SEND_WP_BUSINESS_MESSAGE"] {
                    .block-icon .blockTypeText {
                        color: #fff;
                    }
                }
                
                .current-indicator {
                    position: absolute;
                    right: -5px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 30;
                    
                    .pulse-dot {
                        width: 12px;
                        height: 12px;
                        background: #006cff;
                        border-radius: 50%;
                        box-shadow: 0 0 0 0 rgba(0, 108, 255, 0.7);
                        animation: pulse-dot 2s ease-in-out infinite;
                    }
                    
                    @keyframes pulse-dot {
                        0% {
                            box-shadow: 0 0 0 0 rgba(0, 108, 255, 0.7);
                        }
                        70% {
                            box-shadow: 0 0 0 10px rgba(0, 108, 255, 0);
                        }
                        100% {
                            box-shadow: 0 0 0 0 rgba(0, 108, 255, 0);
                        }
                    }
                }
                
                .done-indicator {
                    position: absolute;
                    top: -15px;
                    right: -15px;
                    z-index: 35;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                    
                    .done-checkmark {
                        width: 32px;
                        height: 32px;
                        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #fff;
                        font-size: 20px;
                        font-weight: bold;
                        box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
                        animation: done-checkmark-bounce 0.6s ease-out;
                    }
                    
                    .done-text {
                        background: #22c55e;
                        color: #fff;
                        padding: 2px 8px;
                        border-radius: 4px;
                        font-size: 10px;
                        font-weight: 700;
                        letter-spacing: 0.5px;
                        text-transform: uppercase;
                        box-shadow: 0 2px 6px rgba(34, 197, 94, 0.3);
                        animation: done-text-fade 0.8s ease-out;
                    }
                    
                    @keyframes done-checkmark-bounce {
                        0% {
                            transform: scale(0);
                        }
                        50% {
                            transform: scale(1.2);
                        }
                        100% {
                            transform: scale(1);
                        }
                    }
                    
                    @keyframes done-text-fade {
                        0% {
                            opacity: 0;
                            transform: translateY(-5px);
                        }
                        100% {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                }
                
                .passed-indicator {
                    position: absolute;
                    right: -5px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 30;
                    
                    .material-icons {
                        font-size: 16px;
                        color: #22c55e;
                    }
                }
            }
        }
    }
}

// Dark mode
@media (prefers-color-scheme: dark) {
    .chatbot-state-viewer {
        background: #1f2937;
        
        .viewer-toolbar {
            background: #111827;
            border-bottom-color: #374151;
            
            .toolbar-info .step-counter {
                color: #9ca3af;
            }
        }
        
        .diagram-map {
            background-image: linear-gradient(rgba(100, 100, 100, 0.2) 1px, transparent 1px),
                linear-gradient(to right, rgba(100, 100, 100, 0.4) 1px, rgba(50, 50, 50, 0.1) 1px) !important;

            .map-wrapper .cb-block .block-caption .block-caption-name {
                color: #e5e7eb;
            }
            
            .map-block {
                background: #1f2937;
                border-color: #374151;
                
                &.block-passed {
                    background: #064e3b;
                    border-color: #22c55e;
                }
                
                &.block-current {
                    background: #1e3a5f;
                    border-color: #3b82f6;
                }
                
                .block-content {
                    .block-name {
                        color: #f9fafb;
                    }
                    
                    .block-type {
                        color: #9ca3af;
                    }
                }
            }
        }
    }
}
</style>
